import type { FC, ReactNode } from 'react'
import type { ProductSummary } from '@/types'

import Link from 'next/link'
import { Sofa } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PriceTag } from '@/components/catalog/price-tag'

interface ProductCardProps {
    product: ProductSummary
    className?: string
}

export const ProductCard: FC<ProductCardProps> = ({ product, className }): ReactNode => {
    const href = `/products/${product.slug}`

    return (
        <article className={cn('group flex flex-col', className)}>
            <Link
                href={href}
                tabIndex={-1}
                aria-hidden='true'
                className='relative block aspect-[4/5] overflow-hidden rounded-2xl bg-secondary'
            >
                {product.image ? (
                    <img
                        src={product.image}
                        alt={product.image_alt ?? product.name}
                        loading='lazy'
                        decoding='async'
                        className='size-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]'
                    />
                ) : (
                    <span className='flex size-full items-center justify-center text-muted-foreground'>
                        <Sofa className='size-10' />
                    </span>
                )}

                {product.is_new && (
                    <span className='absolute start-4 top-4 rounded-full bg-background/90 px-3 py-1 text-xs font-medium text-foreground shadow-sm backdrop-blur'>
                        جدید
                    </span>
                )}
            </Link>

            <div className='mt-5 flex flex-1 flex-col'>
                {product.category_id && product.category_name && (
                    <p className='text-xs font-medium text-brand'>
                        <span data-mdlfy-cms={`categories:${product.category_id}:name`}>
                            {product.category_name}
                        </span>
                    </p>
                )}

                <h3 className='mt-2 font-heading text-2xl font-semibold leading-snug text-foreground'>
                    <Link href={href} className='rounded-md outline-none transition-colors hover:text-brand focus-visible:ring-3 focus-visible:ring-ring/50'>
                        <span data-mdlfy-cms={`products:${product.id}:name`}>
                            {product.name}
                        </span>
                    </Link>
                </h3>

                {product.short_description && (
                    <p data-mdlfy-cms={`products:${product.id}:short_description`} className='mt-2 line-clamp-2 text-sm leading-7 text-muted-foreground'>
                        {product.short_description}
                    </p>
                )}

                <PriceTag price={product.price} showFrom className='mt-auto pt-4' />
            </div>
        </article>
    )
}
